import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { MediaImageSized } from '@/features/media/components/MediaImageSized';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import type { DetailImageEntry } from '@/features/profile-setting/components/organization-edit/types';
import { FixedSizeCarousel } from '@/shared/components/carousel/FixedSizeCarousel';
import type { CarouselLayoutSize } from '@/shared/types/carousel';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = {
  entries: DetailImageEntry[];
  mediaSize?: MediaItemSize;
  imageHeight?: number;
};

/**
 * Carousel for the edit-page preview: renders a mixed list of already-uploaded
 * ClientMediaItems and locally-picked images (still uploading or not yet
 * committed). Reuses MediaImageSized so the preview matches the form's
 * rounded-corner look.
 */
export const PreviewImageCarousel: FC<Props> = ({
  entries,
  mediaSize = MediaItemSize.MEDIUM,
  imageHeight = 260
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const heightStyle = useMemo(
    () => ({ height: imageHeight + 32 }),
    [imageHeight]
  );

  const renderItem = useCallback(
    (entry: DetailImageEntry, layout: CarouselLayoutSize) => {
      const mediaItem: ClientMediaItem | undefined = entry.mediaItem;
      return (
        <MediaImageSized
          style={layout}
          mediaItem={mediaItem}
          localImageUri={entry.asset?.uri}
          mediaSize={mediaSize}
        />
      );
    },
    [mediaSize]
  );

  return (
    <View style={[styles.wrapper, heightStyle]}>
      <FixedSizeCarousel data={entries} renderItem={renderItem} itemGap={16} />
    </View>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    wrapper: {
      backgroundColor: theme.colors.surfaceContainerLowest
    }
  });
